import { BOOLEAN_STRINGS_CONSTANTS } from '../../constants/constants';
import { getWorkEntryEndSortTimestamp } from '../adapters/shared';
import type { WorkEntryFilterOptions, WorkEntryRow } from '../types';

/** Sort work-entry rows by source `dateRange.end` (latest first). */
export function sortWorkEntryRowsByEndDate<TSource>(
    rows: ReadonlyArray<WorkEntryRow<TSource>>
): WorkEntryRow<TSource>[] {
    return [...rows].sort((a, b) => getWorkEntryEndSortTimestamp(b.source) - getWorkEntryEndSortTimestamp(a.source));
}

export function workEntryFromGridParams<TSource>(
    rows: ReadonlyArray<WorkEntryRow<TSource>>,
    params: { id: string | number }
): WorkEntryRow<TSource> | undefined {
    const entryId = String(params.id);
    return rows.find((row) => row.id === entryId);
}

/**
 * Apply a custom drag-and-drop ordering to a list of rows. Rows whose ids appear
 * in `orderedIds` come first, in that order; rows not yet placed (e.g., newly
 * arrived from the backend) are appended to the end.
 */
export function applyWorkEntryOrder<TSource>(
    rows: ReadonlyArray<WorkEntryRow<TSource>>,
    orderedIds: ReadonlyArray<string>
): WorkEntryRow<TSource>[] {
    const byId = new Map(rows.map((row) => [row.id, row]));
    const ordered = orderedIds
        .map((id) => byId.get(id))
        .filter((row): row is WorkEntryRow<TSource> => row !== undefined);
    const placed = new Set(ordered.map((row) => row.id));
    const missing = rows.filter((row) => !placed.has(row.id));
    return [...ordered, ...missing];
}

/**
 * Reconcile an existing ordering with an incoming list of ids. Preserves the
 * order of ids that still exist and appends new ids at the end.
 */
export function reconcileWorkEntryOrder(previous: ReadonlyArray<string>, incoming: ReadonlyArray<string>): string[] {
    if (previous.length === 0) return [...incoming];
    const incomingSet = new Set(incoming);
    const kept = previous.filter((id) => incomingSet.has(id));
    const keptSet = new Set(kept);
    const added = incoming.filter((id) => !keptSet.has(id));
    return [...kept, ...added];
}

/**
 * Apply search, type, year, key and footer toggle filters to an ordered list of
 * rows. Order is preserved. Unset/empty options act as a pass-through.
 */
export function filterWorkEntryRows<TSource>(
    rows: ReadonlyArray<WorkEntryRow<TSource>>,
    options: WorkEntryFilterOptions = {}
): WorkEntryRow<TSource>[] {
    let list: WorkEntryRow<TSource>[] = [...rows];

    const query = options.searchQuery?.trim().toLowerCase();
    if (query) {
        list = list.filter((row) => row.searchText.includes(query));
    }

    if (options.typeFilter && options.typeFilter.length > 0) {
        const types = options.typeFilter;
        list = list.filter((row) => row.typeValue != null && types.includes(row.typeValue));
    }

    if (options.yearFilter && options.yearFilter.length > 0) {
        const selectedYears = options.yearFilter;
        list = list.filter((row) => selectedYears.some((year) => row.years.includes(year)));
    }

    if (options.footerToggle?.active) {
        const { selectedIds, enableSelection } = options.footerToggle;
        list = list.filter(({ id }) => enableSelection && selectedIds.has(id));
    }

    if (options.keyFilter && options.keyFilter.length > 0) {
        const { true: trueValue, false: falseValue } = BOOLEAN_STRINGS_CONSTANTS;
        const matchesTrue = options.keyFilter.includes(trueValue);
        const matchesFalse = options.keyFilter.includes(falseValue);
        if (!(matchesTrue && matchesFalse)) {
            list = list.filter((row) => {
                const isKey = !!row.isKey;
                if (matchesTrue) return isKey;
                if (matchesFalse) return !isKey;
                return true;
            });
        }
    }

    return list;
}

/**
 * Reorder ids inside a visible (possibly filtered) subset and merge the result
 * back into the full ordered id list. Ids outside the visible subset keep their
 * positions. Returns `null` when nothing is visible to reorder.
 */
export function reorderWorkEntryIds(
    orderedIds: ReadonlyArray<string>,
    visibleIds: ReadonlyArray<string>,
    oldIndex: number,
    targetIndex: number
): string[] | null {
    if (!visibleIds.length) return null;

    const movedVisible = [...visibleIds];
    const [movedId] = movedVisible.splice(oldIndex, 1);
    movedVisible.splice(targetIndex, 0, movedId);

    const visibleSet = new Set(visibleIds);
    const nextOrderedIds: string[] = [];
    let visiblePointer = 0;
    orderedIds.forEach((id) => {
        if (visibleSet.has(id)) {
            nextOrderedIds.push(movedVisible[visiblePointer]);
            visiblePointer += 1;
        } else {
            nextOrderedIds.push(id);
        }
    });
    return nextOrderedIds;
}

export function estimateRoleChipTileWidth(labels: readonly string[]): number {
    if (!labels.length) return 56;
    const MIN_CHIP_WIDTH = 44;
    const MAX_CHIP_WIDTH = 120;
    const CHAR_WIDTH = 6.5;
    const HORIZONTAL_PADDING = 16;
    const MIN_VISIBLE_LABEL_RATIO = 0.55;

    return Math.max(
        ...labels.map((label) => {
            const labelWidth = Math.ceil(label.length * CHAR_WIDTH);
            const minVisibleLabelWidth = Math.ceil(labelWidth * MIN_VISIBLE_LABEL_RATIO);
            const targetVisibleWidth = minVisibleLabelWidth + HORIZONTAL_PADDING;
            return Math.min(Math.max(targetVisibleWidth, MIN_CHIP_WIDTH), MAX_CHIP_WIDTH);
        })
    );
}
